/**
 * Cookie-consent manager (DSGVO / GDPR).
 *
 * Central source of truth for which cookie categories the visitor has allowed.
 * Third-party tools that require consent MUST be loaded through this module so
 * they only ever run after the matching category was granted.
 *
 * Usage for a future tool (e.g. analytics):
 *
 *   import { whenConsent } from '@/Lib/consent';
 *   whenConsent('statistics', () => {
 *       // inject / init the tool here — runs immediately if already granted,
 *       // otherwise as soon as the visitor grants "statistics"
 *   });
 *
 * Or inject a script tag directly:
 *
 *   import { loadScriptOnConsent } from '@/Lib/consent';
 *   loadScriptOnConsent('statistics', { src: 'https://…/analytics.js', async: true });
 */

export type ConsentCategory = 'functional' | 'statistics' | 'marketing';

export interface ConsentState {
    functional: boolean;
    statistics: boolean;
    marketing: boolean;
}

/** Bump when the category set or wording changes → re-prompts every visitor. */
export const CONSENT_VERSION = 1;

const COOKIE_NAME = 'tuitjer_cookie_consent';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 182; // ~6 months
const EVENT_UPDATE = 'tuitjer:consent-update';
export const EVENT_OPEN_SETTINGS = 'tuitjer:consent-open';

export const DENY_ALL: ConsentState = { functional: false, statistics: false, marketing: false };
export const GRANT_ALL: ConsentState = { functional: true, statistics: true, marketing: true };

interface StoredConsent extends ConsentState {
    v: number;
    ts: string;
}

/* ── Read ─────────────────────────────────────────────────────────────── */

function readCookie(): StoredConsent | null {
    if (typeof document === 'undefined') return null;

    const match = document.cookie.split('; ').find((row) => row.startsWith(`${COOKIE_NAME}=`));
    if (!match) return null;

    try {
        const parsed = JSON.parse(decodeURIComponent(match.split('=').slice(1).join('='))) as StoredConsent;
        if (parsed.v !== CONSENT_VERSION) return null;
        return parsed;
    } catch {
        return null;
    }
}

/** Current consent, or null when the visitor has not decided yet. */
export function getConsent(): ConsentState | null {
    const stored = readCookie();
    if (!stored) return null;
    return { functional: !!stored.functional, statistics: !!stored.statistics, marketing: !!stored.marketing };
}

export function hasConsent(category: ConsentCategory): boolean {
    return getConsent()?.[category] === true;
}

/* ── Write ────────────────────────────────────────────────────────────── */

export function saveConsent(state: ConsentState): void {
    if (typeof document === 'undefined') return;

    const payload: StoredConsent = { v: CONSENT_VERSION, ts: new Date().toISOString(), ...state };
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(payload))}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;

    // Google Consent Mode v2 — harmless if gtag/GTM are not (yet) present.
    const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event: 'cookie_consent_update', consent: state });
    if (typeof w.gtag === 'function') {
        w.gtag('consent', 'update', {
            analytics_storage: state.statistics ? 'granted' : 'denied',
            ad_storage: state.marketing ? 'granted' : 'denied',
            ad_user_data: state.marketing ? 'granted' : 'denied',
            ad_personalization: state.marketing ? 'granted' : 'denied',
            functionality_storage: state.functional ? 'granted' : 'denied',
            personalization_storage: state.functional ? 'granted' : 'denied',
        });
    }

    window.dispatchEvent(new CustomEvent<ConsentState>(EVENT_UPDATE, { detail: state }));
}

/* ── Subscribe / gate ─────────────────────────────────────────────────── */

export function onConsentChange(cb: (state: ConsentState) => void): () => void {
    if (typeof window === 'undefined') return () => {};
    const handler = (e: Event) => cb((e as CustomEvent<ConsentState>).detail);
    window.addEventListener(EVENT_UPDATE, handler);
    return () => window.removeEventListener(EVENT_UPDATE, handler);
}

/**
 * Run `callback` once the given category is granted — immediately if it already
 * is, otherwise the next time the visitor grants it.
 */
export function whenConsent(category: ConsentCategory, callback: () => void): () => void {
    if (hasConsent(category)) {
        callback();
        return () => {};
    }
    const unsubscribe = onConsentChange((state) => {
        if (state[category]) {
            unsubscribe();
            callback();
        }
    });
    return unsubscribe;
}

/** Inject a <script> tag only after the matching category is granted. */
export function loadScriptOnConsent(
    category: ConsentCategory,
    attrs: { src: string; async?: boolean; defer?: boolean; id?: string } & Record<string, string | boolean>,
): void {
    whenConsent(category, () => {
        if (typeof document === 'undefined') return;
        if (attrs.id && document.getElementById(attrs.id)) return;
        const script = document.createElement('script');
        Object.entries(attrs).forEach(([key, value]) => {
            if (value === true) script.setAttribute(key, '');
            else if (value !== false) script.setAttribute(key, String(value));
        });
        document.head.appendChild(script);
    });
}

/** Open the settings modal from anywhere (e.g. a footer link). */
export function openConsentSettings(): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(EVENT_OPEN_SETTINGS));
}
